<template>
    <div class="weeduc-admin-page corporate-page unit-detail-page text-start p-0" :data-kind="kind">
        <header class="unit-detail-header">
            <div class="unit-detail-header__lead">
                <nav class="unit-detail-breadcrumb" aria-label="Breadcrumb">
                    <template v-for="(crumb, index) in breadcrumbs" :key="`${crumb.label}-${index}`">
                        <router-link
                            v-if="crumb.to"
                            class="unit-detail-breadcrumb__link"
                            :to="crumb.to"
                        >
                            {{ crumb.label }}
                        </router-link>
                        <span v-else class="unit-detail-breadcrumb__current">{{ crumb.label }}</span>
                        <span
                            v-if="index < breadcrumbs.length - 1"
                            class="unit-detail-breadcrumb__sep"
                            aria-hidden="true"
                        >/</span>
                    </template>
                </nav>
                <h1 class="unit-detail-header__title">{{ title }}</h1>
                <p class="unit-detail-header__subtitle">{{ subtitle }}</p>
            </div>
            <div class="unit-detail-header__actions">
                <v-btn
                    variant="tonal"
                    color="primary"
                    rounded="lg"
                    class="text-none"
                    @click="$emit('back')"
                >
                    <v-icon class="me-1" size="small">mdi-arrow-left</v-icon>
                    {{ labels.back }}
                </v-btn>
            </div>
        </header>

        <div class="weeduc-admin-page__shell unit-detail-shell">
            <div v-if="loading" class="unit-detail-skeletons" aria-busy="true" :aria-label="t.common.loading">
                <v-skeleton-loader type="article" class="unit-detail-skeleton" />
                <div class="unit-detail-kpi-grid">
                    <v-skeleton-loader
                        v-for="n in 4"
                        :key="n"
                        type="card"
                        class="unit-detail-skeleton"
                    />
                </div>
                <v-skeleton-loader type="table" class="unit-detail-skeleton" />
            </div>

            <div v-else-if="error" class="unit-detail-empty">
                {{ errorMessage }}
            </div>

            <template v-else>
                <section class="unit-detail-hero" :aria-label="title">
                    <div
                        class="unit-detail-hero__icon"
                        :class="entity.status === 'active' ? 'is-active' : 'is-inactive'"
                        aria-hidden="true"
                    >
                        <fa :icon="kind === 'role' ? 'fa-id-badge' : 'fa-diagram-project'" />
                    </div>
                    <div class="unit-detail-hero__body">
                        <div class="unit-detail-hero__title-row">
                            <h2>{{ entity.name }}</h2>
                            <span
                                class="corporate-detail-chip"
                                :class="entity.status === 'active' ? 'is-success' : 'is-muted'"
                            >
                                {{ entity.status === 'active' ? labels.active : labels.inactive }}
                            </span>
                        </div>
                        <p v-if="entity.description" class="unit-detail-hero__desc">
                            {{ entity.description }}
                        </p>
                        <div class="unit-detail-hero__meta">
                            <span v-if="kind === 'sector' && entity.managerName">
                                <fa icon="fa-user-tie" />
                                {{ labels.manager }}: {{ entity.managerName }}
                            </span>
                            <button
                                v-else-if="kind === 'role' && entity.sectorId"
                                type="button"
                                class="unit-detail-link"
                                @click="$emit('open-sector', entity.sectorId)"
                            >
                                <fa icon="fa-diagram-project" />
                                {{ labels.sector }}: {{ entity.sectorName || '—' }}
                            </button>
                            <span v-else-if="kind === 'role'">
                                <fa icon="fa-diagram-project" />
                                {{ labels.sector }}: {{ entity.sectorName || '—' }}
                            </span>
                            <span>
                                {{ countPhrase(collaborators.length, labels.peopleWordSingular, labels.peopleWord) }}
                                <template v-if="kind === 'sector'">
                                    • {{ countPhrase(roles.length, labels.rolesWordSingular, labels.rolesWord) }}
                                </template>
                                • {{ countPhrase(courseRows.length, labels.coursesWordSingular, labels.coursesWord) }}
                            </span>
                        </div>
                    </div>
                    <div class="unit-detail-hero__progress">
                        <span>{{ labels.avgCompletion }}</span>
                        <strong>{{ completionAvg }}%</strong>
                        <div
                            class="corporate-detail-progress-track"
                            role="progressbar"
                            :aria-valuenow="completionAvg"
                            aria-valuemin="0"
                            aria-valuemax="100"
                            :aria-label="labels.avgCompletion"
                        >
                            <div
                                class="corporate-detail-progress-fill"
                                :class="progressTone(completionAvg)"
                                :style="{ width: `${Math.min(100, completionAvg)}%` }"
                            />
                        </div>
                        <small>
                            {{ withProgressCount }} {{ t.common.of }} {{ collaborators.length }}
                            {{ labels.withProgress }}
                        </small>
                    </div>
                </section>

                <section class="unit-detail-kpi-grid" :aria-label="t.common.kpiIndicators">
                    <article
                        v-for="card in kpiCards"
                        :key="card.key"
                        class="corporate-kpi unit-detail-kpi"
                    >
                        <div
                            class="unit-detail-kpi__icon"
                            :class="`unit-detail-kpi__icon--${card.tone}`"
                            aria-hidden="true"
                        >
                            <fa :icon="card.icon" />
                        </div>
                        <div class="unit-detail-kpi__body">
                            <span class="corporate-kpi__label">{{ card.label }}</span>
                            <strong class="corporate-kpi__value">{{ card.value }}</strong>
                            <span class="unit-detail-kpi__hint">{{ card.hint }}</span>
                        </div>
                    </article>
                </section>

                <section v-if="secondaryKpis.length" class="unit-detail-secondary" :aria-label="t.common.secondaryKpiIndicators">
                    <article
                        v-for="card in secondaryKpis"
                        :key="card.key"
                        class="unit-detail-secondary__item"
                    >
                        <span>{{ card.label }}</span>
                        <strong>{{ card.value }}</strong>
                    </article>
                </section>

                <div class="unit-detail-overview-grid">
                    <section class="unit-detail-panel" :aria-labelledby="perfTitleId">
                        <div class="unit-detail-panel__head">
                            <h3 :id="perfTitleId">{{ performanceTitle }}</h3>
                        </div>
                        <div class="unit-detail-perf">
                            <div class="unit-detail-perf__head">
                                <span>{{ labels.overallProgress }}</span>
                                <strong>{{ completionAvg }}%</strong>
                            </div>
                            <div class="corporate-detail-progress-track unit-detail-perf__bar">
                                <div
                                    class="corporate-detail-progress-fill"
                                    :class="progressTone(completionAvg)"
                                    :style="{ width: `${Math.min(100, completionAvg)}%` }"
                                />
                            </div>
                            <ul class="unit-detail-perf__stats">
                                <li>
                                    <span>{{ labels.notStarted }}</span>
                                    <strong>{{ stageCounts.notStarted }}</strong>
                                </li>
                                <li>
                                    <span>{{ labels.inProgress }}</span>
                                    <strong>{{ stageCounts.inProgress }}</strong>
                                </li>
                                <li>
                                    <span>{{ labels.completed }}</span>
                                    <strong>{{ stageCounts.completed }}</strong>
                                </li>
                            </ul>
                        </div>
                    </section>

                    <section class="unit-detail-panel" :aria-labelledby="attnTitleId">
                        <div class="unit-detail-panel__head">
                            <h3 :id="attnTitleId">{{ labels.attention }}</h3>
                            <p>{{ labels.attentionLead }}</p>
                        </div>
                        <p v-if="!attentionPeople.length" class="unit-detail-panel__empty">
                            {{ labels.attentionEmpty }}
                        </p>
                        <ul v-else class="unit-detail-attention">
                            <li
                                v-for="user in attentionPeople"
                                :key="user.id"
                                class="unit-detail-attention__item"
                            >
                                <div>
                                    <strong>{{ user.name }}</strong>
                                    <span>{{ roleLabel(user) }}</span>
                                    <em>{{ progressLabel(user.progressPercent) }}</em>
                                </div>
                                <button
                                    type="button"
                                    class="unit-detail-text-action"
                                    @click="$emit('open-collaborator', user.id)"
                                >
                                    {{ labels.seeDetails }}
                                    <v-icon size="16">mdi-arrow-right</v-icon>
                                </button>
                            </li>
                        </ul>
                    </section>
                </div>

                <section class="unit-detail-panel">
                    <div class="unit-detail-panel__head">
                        <h3>{{ labels.collaboratorsTab }}</h3>
                    </div>
                    <div class="unit-detail-toolbar">
                        <v-text-field
                            v-model="peopleSearch"
                            :placeholder="labels.searchPeople"
                            variant="outlined"
                            density="compact"
                            hide-details
                            clearable
                            prepend-inner-icon="mdi-magnify"
                            class="weeduc-admin-v-field unit-detail-toolbar__search"
                        />
                        <div class="unit-detail-toolbar__filters">
                            <v-btn-toggle
                                v-model="peopleStatus"
                                mandatory
                                density="compact"
                                divided
                                variant="outlined"
                                color="primary"
                                class="unit-detail-status-toggle"
                            >
                                <v-btn value="all" class="text-none" size="small">{{ labels.filterAll }}</v-btn>
                                <v-btn value="active" class="text-none" size="small">{{ labels.filterActive }}</v-btn>
                                <v-btn value="inactive" class="text-none" size="small">{{ labels.filterInactive }}</v-btn>
                            </v-btn-toggle>
                            <v-select
                                v-if="kind === 'sector' && roleFilterOptions.length > 1"
                                v-model="peopleRoleId"
                                :items="roleFilterOptions"
                                item-title="text"
                                item-value="value"
                                variant="outlined"
                                density="compact"
                                hide-details
                                class="weeduc-admin-v-field unit-detail-toolbar__role"
                            />
                        </div>
                    </div>
                        <div v-if="!filteredPeople.length" class="unit-detail-panel__empty">
                            {{ labels.peopleEmptyTitle }}
                        </div>
                        <div v-else class="corporate-unit-table-wrap">
                            <v-table class="custom-table unit-detail-table">
                                <thead>
                                    <tr>
                                        <th>{{ labels.collaboratorsTab }}</th>
                                        <th v-if="kind === 'sector'">{{ labels.roleCol }}</th>
                                        <th>{{ labels.statusCol }}</th>
                                        <th>{{ labels.progressCol }}</th>
                                        <th v-if="showCoursesCol">{{ labels.coursesTab }}</th>
                                        <th class="text-end" />
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="user in filteredPeople"
                                        :key="user.id"
                                        class="corporate-unit-row"
                                        role="link"
                                        tabindex="0"
                                        @click="$emit('open-collaborator', user.id)"
                                        @keydown.enter.prevent="$emit('open-collaborator', user.id)"
                                    >
                                        <td :data-label="labels.collaboratorsTab">
                                            <div class="unit-detail-person">
                                                <v-avatar size="36" class="unit-detail-person__avatar">
                                                    <v-img v-if="user.photo" :src="user.photo" :alt="user.name" />
                                                    <span v-else>{{ initials(user.name) }}</span>
                                                </v-avatar>
                                                <div>
                                                    <strong>{{ user.name }}</strong>
                                                    <small>{{ user.email }}</small>
                                                </div>
                                            </div>
                                        </td>
                                        <td
                                            v-if="kind === 'sector'"
                                            :data-label="labels.roleCol"
                                        >
                                            {{ roleLabel(user) }}
                                        </td>
                                        <td :data-label="labels.statusCol">
                                            <span
                                                class="corporate-detail-chip"
                                                :class="user.status === 'active' ? 'is-success' : 'is-muted'"
                                            >
                                                {{ user.status === 'active' ? labels.active : labels.inactive }}
                                            </span>
                                        </td>
                                        <td :data-label="labels.progressCol">
                                            <div class="unit-detail-mini-progress">
                                                <strong>{{ user.progressPercent || 0 }}%</strong>
                                                <div class="corporate-detail-progress-track">
                                                    <div
                                                        class="corporate-detail-progress-fill"
                                                        :class="progressTone(user.progressPercent)"
                                                        :style="{
                                                            width: `${Math.min(100, user.progressPercent || 0)}%`,
                                                        }"
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        <td
                                            v-if="showCoursesCol"
                                            :data-label="labels.coursesTab"
                                        >
                                            {{ courseCountLabel(user) }}
                                        </td>
                                        <td class="text-end unit-detail-table__action" @click.stop>
                                            <button
                                                type="button"
                                                class="unit-detail-text-action"
                                                @click="$emit('open-collaborator', user.id)"
                                            >
                                                {{ labels.seeDetails }}
                                                <v-icon size="16">mdi-arrow-right</v-icon>
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </div>
                </section>

                <section v-if="kind === 'sector'" class="unit-detail-panel">
                    <div class="unit-detail-panel__head">
                        <h3>{{ labels.rolesTab }}</h3>
                    </div>
                    <div v-if="!roles.length" class="unit-detail-empty-state">
                        <v-icon size="36" color="#98A2B3">mdi-badge-account-horizontal-outline</v-icon>
                        <h4>{{ labels.rolesEmptyTitle }}</h4>
                        <p>{{ labels.rolesEmptyLead }}</p>
                    </div>
                    <div v-else class="corporate-unit-table-wrap">
                        <v-table class="custom-table unit-detail-table">
                            <thead>
                                <tr>
                                    <th>{{ labels.rolesTab }}</th>
                                    <th>{{ labels.collaboratorsTab }}</th>
                                    <th>{{ labels.coursesTab }}</th>
                                    <th>{{ labels.statusCol }}</th>
                                    <th class="text-end" />
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="role in roles"
                                    :key="role.id"
                                    class="corporate-unit-row"
                                    role="link"
                                    tabindex="0"
                                    @click="$emit('open-role', role.id)"
                                    @keydown.enter.prevent="$emit('open-role', role.id)"
                                >
                                    <td :data-label="labels.rolesTab">
                                        <div class="unit-detail-entity">
                                            <strong>{{ role.name }}</strong>
                                            <small v-if="role.description">{{ role.description }}</small>
                                        </div>
                                    </td>
                                    <td :data-label="labels.collaboratorsTab">
                                        {{ role.collaboratorsCount ?? countUsersInRole(role.id) }}
                                    </td>
                                    <td :data-label="labels.coursesTab">
                                        {{ role.coursesCount ?? (role.courseIds || []).length }}
                                    </td>
                                    <td :data-label="labels.statusCol">
                                        <span
                                            class="corporate-detail-chip"
                                            :class="role.status === 'active' ? 'is-success' : 'is-muted'"
                                        >
                                            {{ role.status === 'active' ? labels.active : labels.inactive }}
                                        </span>
                                    </td>
                                    <td class="text-end unit-detail-table__action" @click.stop>
                                        <button
                                            type="button"
                                            class="unit-detail-text-action"
                                            @click="$emit('open-role', role.id)"
                                        >
                                            {{ labels.seeDetails }}
                                            <v-icon size="16">mdi-arrow-right</v-icon>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </v-table>
                    </div>
                </section>

                <section class="unit-detail-panel">
                    <div class="unit-detail-panel__head">
                        <h3>{{ labels.coursesTab }}</h3>
                    </div>
                    <div v-if="!courseRows.length" class="unit-detail-empty-state">
                        <v-icon size="36" color="#98A2B3">mdi-book-open-page-variant-outline</v-icon>
                        <h4>{{ labels.coursesEmptyTitle }}</h4>
                        <p>{{ labels.coursesEmptyLead }}</p>
                    </div>
                    <div v-else class="corporate-unit-table-wrap">
                        <v-table class="custom-table unit-detail-table">
                            <thead>
                                <tr>
                                    <th>{{ labels.coursesTab }}</th>
                                    <th>{{ labels.collaboratorsTab }}</th>
                                    <th>{{ labels.progressCol }}</th>
                                    <th>{{ labels.completed }}</th>
                                    <th>{{ labels.statusCol }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="course in courseRows" :key="course.courseId">
                                    <td :data-label="labels.coursesTab">
                                        <div class="unit-detail-entity">
                                            <strong>{{ course.courseName }}</strong>
                                        </div>
                                    </td>
                                    <td :data-label="labels.collaboratorsTab">
                                        {{ course.assigned || 0 }}
                                    </td>
                                    <td :data-label="labels.progressCol">
                                        <div class="unit-detail-mini-progress">
                                            <strong>{{ course.completionRate || 0 }}%</strong>
                                            <div class="corporate-detail-progress-track">
                                                <div
                                                    class="corporate-detail-progress-fill"
                                                    :class="progressTone(course.completionRate)"
                                                    :style="{
                                                        width: `${Math.min(100, course.completionRate || 0)}%`,
                                                    }"
                                                />
                                            </div>
                                        </div>
                                    </td>
                                    <td :data-label="labels.completed">
                                        {{ course.completed || 0 }} / {{ course.assigned || 0 }}
                                    </td>
                                    <td :data-label="labels.statusCol">
                                        <span
                                            class="corporate-detail-chip"
                                            :class="courseStatusChip(course)"
                                        >
                                            {{ courseStatusLabel(course) }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </v-table>
                    </div>
                </section>
            </template>
        </div>
    </div>
</template>

<script>
import { corporateI18n } from '@/i18n/mixins';

export default {
    name: 'UnitDetailLayout',
    mixins: [corporateI18n],
    props: {
        kind: { type: String, required: true },
        entity: { type: Object, default: () => ({}) },
        collaborators: { type: Array, default: () => [] },
        roles: { type: Array, default: () => [] },
        courseRows: { type: Array, default: () => [] },
        completionAvg: { type: Number, default: 0 },
        loading: { type: Boolean, default: false },
        error: { type: [Boolean, String], default: false },
        errorMessage: { type: String, default: '' },
        breadcrumbs: { type: Array, default: () => [] },
        title: { type: String, default: '' },
        subtitle: { type: String, default: '' },
        labels: { type: Object, required: true },
        roleLabelFn: { type: Function, default: null },
    },
    emits: ['back', 'open-collaborator', 'open-role', 'open-sector'],
    data() {
        return {
            peopleSearch: '',
            peopleStatus: 'all',
            peopleRoleId: '',
        };
    },
    computed: {
        perfTitleId() {
            return `unit-perf-${this.kind}`;
        },
        attnTitleId() {
            return `unit-attn-${this.kind}`;
        },
        performanceTitle() {
            return this.kind === 'role' ? this.labels.performanceRole : this.labels.performance;
        },
        activePeople() {
            return this.collaborators.filter((u) => u.status === 'active').length;
        },
        inactiveRoles() {
            return this.roles.filter((r) => r.status !== 'active').length;
        },
        withProgressCount() {
            return this.collaborators.filter((u) => Number(u.progressPercent) > 0).length;
        },
        stageCounts() {
            let notStarted = 0;
            let inProgress = 0;
            let completed = 0;
            this.collaborators.forEach((user) => {
                const n = Number(user.progressPercent) || 0;
                if (n >= 100) completed += 1;
                else if (n > 0) inProgress += 1;
                else notStarted += 1;
            });
            return { notStarted, inProgress, completed };
        },
        attentionPeople() {
            return this.collaborators
                .filter((user) => (Number(user.progressPercent) || 0) < 30)
                .slice(0, 6);
        },
        kpiCards() {
            const activeWord = this.activePeople === 1
                ? this.labels.activeSingular
                : this.labels.activePlural;
            const peopleHint = `${this.activePeople} ${activeWord}`;
            let rolesHint = this.labels.allRolesActive;
            if (this.inactiveRoles === 1) rolesHint = `1 ${this.labels.inactiveRole}`;
            else if (this.inactiveRoles > 1) rolesHint = `${this.inactiveRoles} ${this.labels.inactiveRoles}`;
            const coursesHint = this.courseRows.length
                ? `${this.courseRows.length} ${this.labels.coursesWord}`
                : this.labels.noCourses;
            const cards = [
                {
                    key: 'people',
                    label: this.labels.collaboratorsTab,
                    value: this.collaborators.length,
                    hint: peopleHint,
                    icon: 'fa-users',
                    tone: 'people',
                },
            ];
            if (this.kind === 'sector') {
                cards.push({
                    key: 'roles',
                    label: this.labels.rolesTab,
                    value: this.roles.length,
                    hint: rolesHint,
                    icon: 'fa-id-badge',
                    tone: 'roles',
                });
            }
            cards.push(
                {
                    key: 'courses',
                    label: this.labels.assignedCourses,
                    value: this.courseRows.length,
                    hint: coursesHint,
                    icon: 'fa-book-open',
                    tone: 'courses',
                },
                {
                    key: 'completion',
                    label: this.labels.avgCompletion,
                    value: `${this.completionAvg}%`,
                    hint: this.kind === 'role' ? this.labels.roleProgress : this.labels.sectorProgress,
                    icon: 'fa-chart-line',
                    tone: 'progress',
                },
            );
            return cards.slice(0, 4);
        },
        secondaryKpis() {
            return [
                { key: 'running', label: this.labels.inProgress, value: this.stageCounts.inProgress },
                { key: 'done', label: this.labels.completed, value: this.stageCounts.completed },
            ];
        },
        roleFilterOptions() {
            const mapped = (this.roles || [])
                .map((role) => ({ text: role.name, value: String(role.id) }))
                .filter((opt) => opt.value);
            return [{ text: this.labels.allRoles, value: '' }, ...mapped];
        },
        showCoursesCol() {
            return this.collaborators.some((user) => {
                if (user.completedCourses != null || user.enrolmentsCount != null) return true;
                const enrolments = user.enrolments || user.progress?.enrolments;
                return Array.isArray(enrolments);
            });
        },
        filteredPeople() {
            const term = String(this.peopleSearch || '').trim().toLowerCase();
            return this.collaborators.filter((user) => {
                if (this.peopleStatus === 'active' && user.status !== 'active') return false;
                if (this.peopleStatus === 'inactive' && user.status === 'active') return false;
                if (this.peopleRoleId && String(user.roleId) !== String(this.peopleRoleId)) return false;
                if (!term) return true;
                return [user.name, user.email, this.roleLabel(user)]
                    .some((value) => String(value || '').toLowerCase().includes(term));
            });
        },
    },
    watch: {
        kind() {
            this.peopleSearch = '';
            this.peopleStatus = 'all';
            this.peopleRoleId = '';
        },
    },
    methods: {
        countPhrase(count, singular, plural) {
            const n = Number(count) || 0;
            return `${n} ${n === 1 ? (singular || plural) : plural}`;
        },
        progressLabel(value) {
            return `${Number(value) || 0}% ${this.labels.progressSuffix}`;
        },
        initials(name) {
            return String(name || '')
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map((part) => part[0])
                .join('')
                .toUpperCase();
        },
        progressTone(value) {
            const n = Number(value) || 0;
            if (n >= 80) return 'is-high';
            if (n >= 40) return 'is-medium';
            return 'is-low';
        },
        countUsersInRole(roleId) {
            return this.collaborators.filter((u) => String(u.roleId) === String(roleId)).length;
        },
        roleLabel(user) {
            if (this.roleLabelFn) return this.roleLabelFn(user);
            return user?.roleName || '—';
        },
        courseCountLabel(user) {
            const enrolments = user.enrolments || user.progress?.enrolments;
            if (Array.isArray(enrolments) && enrolments.length) {
                const done = enrolments.filter((item) => item.status === 'completed').length;
                return `${done} / ${enrolments.length} ${this.labels.completed.toLowerCase()}`;
            }
            const done = Number(user.completedCourses);
            const total = Number(user.enrolmentsCount);
            if (Number.isFinite(done) && Number.isFinite(total) && total > 0) {
                return `${done} / ${total} ${this.labels.completed.toLowerCase()}`;
            }
            if (Number.isFinite(done)) return String(done);
            return '—';
        },
        courseStatusLabel(course) {
            const assigned = Number(course.assigned) || 0;
            const completed = Number(course.completed) || 0;
            const inProgress = Number(course.inProgress) || 0;
            if (assigned > 0 && completed >= assigned) return this.labels.courseStatusDone;
            if (completed > 0 || inProgress > 0) return this.labels.courseStatusProgress;
            return this.labels.courseStatusIdle;
        },
        courseStatusChip(course) {
            const label = this.courseStatusLabel(course);
            if (label === this.labels.courseStatusDone) return 'is-success';
            if (label === this.labels.courseStatusProgress) return 'is-info';
            return 'is-muted';
        },
    },
};
</script>

<style src="@/css/weeduc/admin/corporate-detail-shared.css"></style>
<style src="@/css/weeduc/admin/corporate.css"></style>
